import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import * as ai from "../../services/aiAssistantService";
import {
  DAYS,
  MODES,
  formToHours,
  hoursToForm,
  languageLabel,
  settingsChanged,
  timeZoneChoices,
  validateHoursForm
} from "./aiHelpers";

const toForm = (s) => ({
  mode: s.mode,
  languages: s.languages,
  business_name: s.business_name || "",
  greeting: s.greeting || "",
  tone_notes: s.tone_notes || "",
  timezone: s.timezone,
  hoursForm: hoursToForm(s.support_hours),
  holidays: s.holidays || []
});

// The form compared with what was saved, in the one shape settingsChanged() understands.
const comparable = (f) => ({
  mode: f.mode,
  languages: f.languages,
  business_name: f.business_name,
  greeting: f.greeting,
  tone_notes: f.tone_notes,
  timezone: f.timezone,
  hours: formToHours(f.hoursForm),
  holidays: f.holidays
});

const SettingsTab = ({ data, onSaved }) => {
  const settings = data.settings;
  const saved = useMemo(() => toForm(settings), [settings]);
  // The form starts from what was saved. After a save the page gives this component a new `key`, so it
  // starts again from the new values: no effect copying props into state (it could land after an edit and
  // quietly undo it).
  const [form, setForm] = useState(saved);
  const [newHoliday, setNewHoliday] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setDay = (key, patch) => setForm((f) => ({ ...f, hoursForm: { ...f.hoursForm, [key]: { ...f.hoursForm[key], ...patch } } }));

  const hourErrors = validateHoursForm(form.hoursForm);
  const changed = settingsChanged(comparable(toForm(settings)), comparable(form));

  const toggleLanguage = (code) =>
    set({ languages: form.languages.includes(code) ? form.languages.filter((l) => l !== code) : [...form.languages, code] });

  const addHoliday = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newHoliday)) return;
    if (!form.holidays.includes(newHoliday)) set({ holidays: [...form.holidays, newHoliday].sort() });
    setNewHoliday("");
  };

  const save = async () => {
    if (form.mode === "live" && settings.mode !== "live") {
      const sure = window.confirm("Live mode: the assistant will reply to your customers on WhatsApp by itself. Switch it on?");
      if (!sure) return;
    }
    setSaving(true);
    try {
      const res = await ai.saveSettings({
        mode: form.mode,
        languages: form.languages,
        business_name: form.business_name,
        greeting: form.greeting,
        tone_notes: form.tone_notes,
        timezone: form.timezone,
        support_hours: formToHours(form.hoursForm),
        holidays: form.holidays
      });
      if (res && res.success) {
        toast.success("Saved");
        onSaved(res.data);
      } else {
        toast.error((res && res.message) || "Could not save");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="row g-3">
      <div className="col-12">
        <div className="card shadow-sm rounded-4">
          <div className="card-body">
            <h6 className="fw-semibold mb-3">When should the assistant answer?</h6>
            {MODES.map((m) => (
              <div className="form-check mb-2" key={m.value}>
                <input
                  className="form-check-input"
                  type="radio"
                  name="ai-mode"
                  id={`ai-mode-${m.value}`}
                  checked={form.mode === m.value}
                  onChange={() => set({ mode: m.value })}
                />
                <label className="form-check-label" htmlFor={`ai-mode-${m.value}`}>
                  <span className="fw-semibold">{m.label}</span>
                  <span className="d-block text-muted small">{m.help}</span>
                </label>
              </div>
            ))}
            <p className="text-muted small mb-0 mt-3">
              The assistant stays out of any chat that is assigned to a team member, and hands a chat over when a customer asks for a person, complains,
              or wants to book. It cannot make, change or cancel a booking itself yet.
            </p>
          </div>
        </div>
      </div>

      <div className="col-12 col-lg-6">
        <div className="card shadow-sm rounded-4 h-100">
          <div className="card-body">
            <h6 className="fw-semibold mb-3">Your business</h6>
            <label className="form-label small mb-1" htmlFor="ai-name">
              Business name the assistant uses
            </label>
            <input id="ai-name" className="form-control mb-3" maxLength={120} value={form.business_name} onChange={(e) => set({ business_name: e.target.value })} />
            <label className="form-label small mb-1" htmlFor="ai-greeting">
              Greeting <span className="text-muted">(optional)</span>
            </label>
            <textarea id="ai-greeting" className="form-control mb-3" rows={2} maxLength={300} value={form.greeting} onChange={(e) => set({ greeting: e.target.value })} />
            <label className="form-label small mb-1" htmlFor="ai-tone">
              Tone and style <span className="text-muted">(optional)</span>
            </label>
            <textarea
              id="ai-tone"
              className="form-control"
              rows={2}
              maxLength={500}
              placeholder="For example: friendly and brief, address customers as Sir or Madam"
              value={form.tone_notes}
              onChange={(e) => set({ tone_notes: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="col-12 col-lg-6">
        <div className="card shadow-sm rounded-4 h-100">
          <div className="card-body">
            <h6 className="fw-semibold mb-3">Languages</h6>
            {settings.languages_available.map((code) => (
              <div className="form-check" key={code}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`ai-lang-${code}`}
                  checked={form.languages.includes(code)}
                  disabled={code === "en"}
                  onChange={() => toggleLanguage(code)}
                />
                <label className="form-check-label" htmlFor={`ai-lang-${code}`}>
                  {languageLabel(code)}
                  {code === "en" && <span className="text-muted small"> (always on)</span>}
                </label>
              </div>
            ))}
            <p className="text-muted small mt-3 mb-0">
              If a customer writes in a language that is not switched on, they get one short reply in English and your team takes over. More languages
              are added as each one is tested.
            </p>
          </div>
        </div>
      </div>

      <div className="col-12">
        <div className="card shadow-sm rounded-4">
          <div className="card-body">
            <h6 className="fw-semibold mb-1">Support hours</h6>
            <p className="text-muted small">
              Outside these hours the assistant does not promise a person: it offers a callback for when you are back.
            </p>
            <div className="row g-2 align-items-end mb-3">
              <div className="col-12 col-md-4">
                <label className="form-label small mb-1" htmlFor="ai-tz">
                  Time zone
                </label>
                <select id="ai-tz" className="form-select" value={form.timezone} onChange={(e) => set({ timezone: e.target.value })}>
                  {timeZoneChoices(form.timezone).map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {DAYS.map(({ key, label }) => {
              const row = form.hoursForm[key];
              return (
                <div className="d-flex align-items-center gap-3 mb-2 flex-wrap" key={key}>
                  <div className="form-check" style={{ width: 150 }}>
                    <input className="form-check-input" type="checkbox" id={`ai-day-${key}`} checked={row.open} onChange={(e) => setDay(key, { open: e.target.checked })} />
                    <label className="form-check-label" htmlFor={`ai-day-${key}`}>
                      {label}
                    </label>
                  </div>
                  {row.open ? (
                    <div className="d-flex align-items-center gap-2">
                      <input type="time" aria-label={`${label} opens`} className="form-control form-control-sm" value={row.from} onChange={(e) => setDay(key, { from: e.target.value })} />
                      <span className="text-muted small">to</span>
                      <input type="time" aria-label={`${label} closes`} className="form-control form-control-sm" value={row.to} onChange={(e) => setDay(key, { to: e.target.value })} />
                    </div>
                  ) : (
                    <span className="text-muted small">Closed</span>
                  )}
                </div>
              );
            })}
            {hourErrors.length > 0 && (
              <ul className="text-danger small mt-2 mb-0">
                {hourErrors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}

            <hr />
            <h6 className="fw-semibold mb-2">Holidays</h6>
            <div className="d-flex gap-2 mb-2" style={{ maxWidth: 360 }}>
              <input type="date" aria-label="Holiday date" className="form-control form-control-sm" value={newHoliday} onChange={(e) => setNewHoliday(e.target.value)} />
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addHoliday} disabled={!newHoliday}>
                Add
              </button>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {form.holidays.length === 0 && <span className="text-muted small">No holidays added.</span>}
              {form.holidays.map((d) => (
                <span key={d} className="badge text-bg-light border d-flex align-items-center gap-2" style={{ fontSize: 12 }}>
                  {d}
                  <button type="button" className="btn-close" style={{ fontSize: 8 }} aria-label={`Remove ${d}`} onClick={() => set({ holidays: form.holidays.filter((h) => h !== d) })} />
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="col-12 d-flex align-items-center gap-3">
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving || !changed || hourErrors.length > 0}>
          {saving ? "Saving…" : "Save changes"}
        </button>
        {changed && !saving && (
          <button type="button" className="btn btn-link btn-sm" onClick={() => setForm(saved)}>
            Discard changes
          </button>
        )}
        <span className="text-muted small ms-auto">
          {settings.country ? `Country: ${settings.country}. ` : ""}Set up by UtilStop: country and booking system.
        </span>
      </div>
    </div>
  );
};

export default SettingsTab;
